import { FilterConfigurationBuilder } from './filter-configuration-builder';
import {
  Category,
  ContractType,
  Country,
  FilterConfiguration,
  FilterState,
  Subcategory,
  SubcategoryOption,
} from '@immosky/types';
import { getPriceOptionsForContractType } from '../price/price.state';
import { getUsageSpaceForContractType } from '../usage-space/usage-space.state';
import { getLivingSpaceOptionsForContractType } from '../living-space/living-space.state';
import { getPropertySpaceOptionsForContractType } from '../property-space/property-space.state';
import { getRoomCountOptionsForContractType } from '../room-count/room-count.state';

export class FilterConfigurationFactory {
  private static countries: Country[];
  private static contractType: ContractType;
  private static mainCategory: Category;
  private static subcategories: Subcategory[] = [];
  private static configurationBuilder: FilterConfigurationBuilder;

  static create(filterState: FilterState): FilterConfiguration {
    this.configurationBuilder = new FilterConfigurationBuilder();
    this.contractType = filterState.contractType.filter.value;
    this.mainCategory = filterState.category.main.filter.value;
    this.subcategories = filterState.category.sub.filter[this.mainCategory]
      .filter((o: SubcategoryOption) => o.isChecked)
      .map((o: SubcategoryOption) => o.value);
    this.countries =
      filterState.countries.length > 0 ? filterState.countries : ['CH'];

    this.setDropdownOptions();
    this.setDisplaySubcategory();
    this.setDisplayRoomCount();
    this.setDisplayUsageSpace();
    this.setDisplayLivingSpace();
    this.setDisplayPropertySpace();

    return this.configurationBuilder.build();
  }

  private static setDropdownOptions() {
    this.configurationBuilder.setPriceOptions(
      getPriceOptionsForContractType(this.countries, this.contractType)
    );
    this.configurationBuilder.setLivingSpaceOptions(
      getLivingSpaceOptionsForContractType(this.countries, this.contractType)
    );
    this.configurationBuilder.setPropertySpaceOptions(
      getPropertySpaceOptionsForContractType(this.countries, this.contractType)
    );
    this.configurationBuilder.setRoomCountOptions(
      getRoomCountOptionsForContractType(this.countries, this.contractType)
    );
    this.configurationBuilder.setUsageSpaceOptions(
      getUsageSpaceForContractType(this.contractType)
    );
  }

  private static setDisplaySubcategory() {
    this.configurationBuilder.setDisplaySubcategory(true);

    if (this.countries.indexOf('FR') > -1) {
      this.configurationBuilder.setDisplaySubcategory(false);
    }
  }

  private static setDisplayRoomCount() {
    this.configurationBuilder.setDisplayRoomCount(true);

    switch (this.mainCategory) {
      case 'land':
        this.configurationBuilder.setDisplayRoomCount(false);
        break;
      case 'commercial':
        if (this.isSubcategoryActive('factory')) {
          this.configurationBuilder.setDisplayRoomCount(false);
        }
        break;
    }
  }

  private static setDisplayUsageSpace() {
    this.configurationBuilder.setDisplayUsageSpace(false);

    if (this.countries.indexOf('FR') > -1) {
      this.configurationBuilder.setDisplayUsageSpace(false);
    } else {
      switch (this.mainCategory) {
        case 'income':
          if (!this.isSubcategoryActive('multi-family-house')) {
            this.configurationBuilder.setDisplayUsageSpace(true);
          }
          break;
        case 'commercial':
          this.configurationBuilder.setDisplayUsageSpace(true);
          break;
      }
    }
  }

  private static setDisplayLivingSpace() {
    this.configurationBuilder.setDisplayLivingSpace(true);

    switch (this.mainCategory) {
      case 'land':
        this.configurationBuilder.setDisplayLivingSpace(false);
        break;
      case 'income':
        if (this.isSubcategoryActive('business-house')) {
          this.configurationBuilder.setDisplayLivingSpace(false);
        }
        break;
      case 'commercial':
        this.configurationBuilder.setDisplayLivingSpace(false);

        if (
          this.isSubcategoryActive('commercial-residential-building') ||
          this.isSubcategoryActive('hotel')
        ) {
          this.configurationBuilder.setDisplayLivingSpace(true);
        }
        break;
    }
  }

  private static setDisplayPropertySpace() {
    this.configurationBuilder.setDisplayPropertySpace(true);

    switch (this.mainCategory) {
      case 'flat':
        this.configurationBuilder.setDisplayPropertySpace(false);
        break;
      case 'commercial':
        if (
          this.isSubcategoryActive('office') ||
          this.isSubcategoryActive('shop') ||
          this.isSubcategoryActive('warehouse') ||
          this.isSubcategoryActive('practice')
        ) {
          this.configurationBuilder.setDisplayPropertySpace(false);
        }
        break;
    }
  }

  private static isSubcategoryActive(subcategory: Subcategory): boolean {
    return this.subcategories.indexOf(subcategory) > -1;
  }
}
